import type { Permission } from '../../api/types/permissions';
import { activeGroupId, visibleNavGroups, type NavGroupDef, type NavItemDef } from './navGroups';

// Rutas del menú PLATFORM (las que viven en grupos del acordeón). "Mi perfil" queda SUELTO
// arriba y NO entra aquí. Tipado al subconjunto PLATFORM de NavTo.
export type PlatformNavTo =
    | '/platform/tenants'
    | '/platform/applications'
    | '/platform/admins'
    | '/platform/roles'
    | '/platform/branding'
    | '/platform/security'
    | '/platform/audit';

// Tipos de platform = alias de las primitivas genéricas (DRY). Mantienen los nombres exportados
// (PlatformNavItem/PlatformNavGroup) para no romper a los consumidores ni los tests.
export type PlatformNavItem = NavItemDef<PlatformNavTo>;
export type PlatformNavGroup = NavGroupDef<'administracion' | 'clientes', PlatformNavTo>;

// Estructura del menú PLATFORM agrupado. El orden de los items dentro de cada grupo respeta el
// orden funcional pedido (no alfabético: el caso de uso del menú define el orden de las secciones).
// Las rutas, permisos y labels son EXACTAMENTE los que tenía la lista plana anterior.
export const PLATFORM_NAV_GROUPS: PlatformNavGroup[] = [
    {
        // "Administración": gestionar la plataforma en sí (usuarios/roles/branding/seguridad/auditoría
        // DE la plataforma).
        id: 'administracion',
        titleKey: 'common:nav.platform.groups.administracion',
        items: [
            {
                to: '/platform/admins',
                permission: 'platform.user.list',
                labelKey: 'common:nav.platform.admins',
            },
            {
                to: '/platform/roles',
                permission: 'platform.role.list',
                labelKey: 'common:nav.platform.roles',
            },
            {
                // NO existe permiso platform.branding.*; el platform admin (scope PLATFORM '*') posee
                // tenant.branding.manage y el backend re-enforza sobre /tenants/platform/branding.
                to: '/platform/branding',
                permission: 'tenant.branding.manage',
                labelKey: 'common:nav.platform.branding',
            },
            {
                // Igual que branding: NO existe permiso platform.security.*; el platform admin
                // (scope PLATFORM '*') posee tenant.security.policy.manage y el backend re-enforza
                // sobre /tenants/platform/security-policy.
                to: '/platform/security',
                permission: 'tenant.security.policy.manage',
                labelKey: 'common:nav.platform.security',
            },
            {
                to: '/platform/audit',
                permission: 'platform.audit.view',
                labelKey: 'common:nav.platform.audit',
            },
        ],
    },
    {
        // "Clientes": el roster de tenants + las plantillas (Aplicaciones) que se les asocian.
        id: 'clientes',
        titleKey: 'common:nav.platform.groups.clientes',
        items: [
            {
                to: '/platform/tenants',
                permission: 'platform.tenant.list',
                labelKey: 'common:nav.platform.tenants',
            },
            {
                to: '/platform/applications',
                permission: 'platform.application.manage',
                labelKey: 'common:nav.platform.applications',
            },
        ],
    },
];

/**
 * Id del grupo cuyo item matchea el pathname actual (auto-abrir el grupo del activo). Delega en la
 * primitiva genérica `activeGroupId`. Match por prefijo: cubre tanto el item EXACTO
 * (`/platform/tenants`) como sus descendientes (`/platform/tenants/abc`). Devuelve null si la ruta
 * no pertenece a ningún grupo PLATFORM.
 */
export function groupIdForPathname(pathname: string): PlatformNavGroup['id'] | null {
    return activeGroupId(PLATFORM_NAV_GROUPS, pathname);
}

/**
 * Grupos PLATFORM visibles para el usuario, con sus items filtrados por permiso. Delega en la
 * primitiva genérica `visibleNavGroups`. Un grupo cuyos items quedan TODOS gateados-out NO aparece
 * (evita el header huérfano). `canSee` debe replicar el chequeo del <Gate> (hasPermission con scope
 * PLATFORM).
 */
export function visiblePlatformGroups(
    canSee: (permission: Permission) => boolean,
): PlatformNavGroup[] {
    return visibleNavGroups(PLATFORM_NAV_GROUPS, canSee);
}
